import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import tradeRegistrationApis from "../../apiConfigs/tradeRegistration";
import { ReactComponent as Delete } from "../../assets/delete.svg";
import { ReactComponent as Edit } from "../../assets/edit.svg";
import DeleteModal from "../../designComponents/DeleteModal";
import LabelInputField from "../../designComponents/LabelInputField/LabelInputField";
import MediaUploader from "../../designComponents/MediaUpload/MediaUpload";
import Modal from "../../designComponents/Modal";
import requestAPI from "../../utils/requestAPI";
import { getIconsUrl } from "../../utils/resizeImages";
import { uploadFilesToS3OnlyKeys } from "../../utils/s3Upload";
import {
  AcceptButton,
  ButtonsSection,
  Card,
  LabelText,
  SectionTitle,
  TitleSection,
} from "../Properties/Details/styles";
import Dropdown from "../../designComponents/Dropdown/Dropdown";

type MODAL_STATE_TYPE = {
  registrationId?: string;
  state: "add" | "edit" | "delete" | undefined;
};

const INITIAL_MODAL_STATE: MODAL_STATE_TYPE = {
  registrationId: undefined,
  state: undefined,
};

export enum REGISTRATION_TYPE {
  VAT = "VAT",
  RGI = "RGI",
  SEAI = "SEAI",
  SAFE_ELECTRIC = "SAFE_ELECTRIC",
}

const RegTypeOptions: { label: string; value: REGISTRATION_TYPE }[] = [
  {
    label: "Vat",
    value: REGISTRATION_TYPE.VAT,
  },
  {
    label: "RGI",
    value: REGISTRATION_TYPE.RGI,
  },
  {
    label: "SEAI",
    value: REGISTRATION_TYPE.SEAI,
  },
  {
    label: "Safe Electric",
    value: REGISTRATION_TYPE.SAFE_ELECTRIC,
  },
];

type TradeRegistrationType = {
  _id?: string;
  regType?: REGISTRATION_TYPE;
  company: string;
  logo: string | File;
};

const TradeRegistrationTypes = () => {
  const [tradeRegistrations, setTradeRegistrations] = useState<
    TradeRegistrationType[]
  >([]);

  const INITIAL_VALUES = {
    company: "",
    documents: [],
    logo: "",
    regNo: "",
    regType: undefined,
  };

  const [initialValues, setInitialValues] =
    useState<TradeRegistrationType>(INITIAL_VALUES);

  const [modalState, setModalState] =
    useState<MODAL_STATE_TYPE>(INITIAL_MODAL_STATE);

  const getAllTradeRegistrations = async () => {
    const response = await requestAPI(
      tradeRegistrationApis.getAllTradeRegistration()
    );
    setTradeRegistrations(response);
  };

  useEffect(() => {
    getAllTradeRegistrations();
  }, []);

  const handleDeleteTradeRegistration = async (id: string) => {
    try {
      await requestAPI(tradeRegistrationApis.deleteTradeRegistration(id));
      toast.success("Successfully Deleted");
      await getAllTradeRegistrations();
      setModalState(INITIAL_MODAL_STATE);
    } catch (error: any) {
      toast.error("Error " + error.message);
    }
  };

  const onSuccessMutations = async () => {
    await getAllTradeRegistrations();
    setModalState(INITIAL_MODAL_STATE);
    setInitialValues(INITIAL_VALUES);
  };

  const handleAddTradeRegistration = async () => {
    let providedUrl;
    try {
      if (initialValues.logo && typeof initialValues.logo !== "string") {
        const uploadFiles = await uploadFilesToS3OnlyKeys({
          category: "assets",
          fileUri: [initialValues.logo],
          assetsCategory: "Registration Types",
        });
        if (uploadFiles && uploadFiles.length > 0) {
          providedUrl = uploadFiles[0];
          await requestAPI(
            tradeRegistrationApis.addNewTradeRegistration({
              ...initialValues,
              logo: providedUrl,
              regType: initialValues.regType || "",
            })
          );
        }
      } else if (typeof initialValues.logo === "string") {
        providedUrl = initialValues.logo;
        await requestAPI(
          tradeRegistrationApis.addNewTradeRegistration({
            ...initialValues,
            logo: providedUrl,
            regType: initialValues.regType || "",
          })
        );
      }
      toast.success("Successfully Added Trade Registration.");
      await onSuccessMutations();
    } catch (error: any) {
      toast.error("Error:" + error.message);
    }
  };

  const handleEditTradeRegistration = async (id: string) => {
    let providedUrl;
    try {
      if (initialValues.logo && typeof initialValues.logo !== "string") {
        const uploadFiles = await uploadFilesToS3OnlyKeys({
          category: "assets",
          fileUri: [initialValues.logo],
          assetsCategory: "Registration Types",
        });
        if (uploadFiles && uploadFiles.length > 0) {
          providedUrl = uploadFiles[0];
          await requestAPI(
            tradeRegistrationApis.updateTradeRegistration(
              initialValues._id || "",
              {
                ...initialValues,
                logo: providedUrl,
                regType: initialValues.regType || "",
              }
            )
          );
        }
      } else if (typeof initialValues.logo === "string") {
        providedUrl = initialValues.logo;
        await requestAPI(
          tradeRegistrationApis.updateTradeRegistration(
            initialValues._id || "",
            {
              ...initialValues,
              logo: providedUrl,
              regType: initialValues.regType || "",
            }
          )
        );
      } else if (!initialValues.logo) {
        await requestAPI(
          tradeRegistrationApis.updateTradeRegistration(
            initialValues._id || "",
            {
              ...initialValues,
              logo: "",
              regType: initialValues.regType || "",
            }
          )
        );
      }
      await onSuccessMutations();
      toast.success("Successfully Updated Trade Registration");
    } catch (error: any) {
      toast.error("Error:" + error.message);
    }
  };

  const getRegTypeOptions = (): {
    label: string;
    value: REGISTRATION_TYPE;
  }[] => {
    const usedTypes = tradeRegistrations
      .filter((tr) => tr._id !== initialValues._id) // exclude the one being edited
      .map((tr) => tr.regType);

    return RegTypeOptions.filter((opt) => {
      if (modalState.state === "edit" && opt.value === initialValues.regType) {
        return true;
      }
      return !usedTypes.includes(opt.value);
    });
  };
  const hasRemainingRegTypes = () => {
    const used = tradeRegistrations.map((tr) => tr.regType);
    return RegTypeOptions.some((opt) => !used.includes(opt.value));
  };
  return (
    <div>
      <Card style={{ margin: 0 }}>
        <TitleSection>
          <ButtonsSection>
            <SectionTitle>Trade Registration Types</SectionTitle>
            {hasRemainingRegTypes() && (
              <AcceptButton
                onClick={() => {
                  setModalState({ state: "add" });
                }}
              >
                Add Trade Registration
              </AcceptButton>
            )}
          </ButtonsSection>
        </TitleSection>
        {tradeRegistrations.map((tReg, index) => {
          return (
            <ButtonsSection key={index}>
              <div
                style={{
                  display: "flex",
                  gap: 40,
                  alignItems: "center",
                }}
              >
                <img
                  src={getIconsUrl(tReg.logo as string)}
                  width={40}
                  height={40}
                  style={{ borderRadius: 40 }}
                  alt={tReg?.company}
                ></img>
                <div>
                  <LabelText>{tReg.company}</LabelText>
                  <LabelText>{tReg.regType}</LabelText>
                </div>
              </div>
              <div style={{ display: "flex", gap: 20, cursor: "pointer" }}>
                <div
                  onClick={() => {
                    setModalState({
                      state: "delete",
                      registrationId: tReg._id,
                    });
                  }}
                >
                  <Delete fill="#7E7E7E"></Delete>
                </div>
                <div
                  onClick={() => {
                    setModalState({ state: "edit", registrationId: tReg?._id });
                    setInitialValues(tReg);
                  }}
                >
                  <Edit fill="#7E7E7E" />
                </div>
              </div>

              <DeleteModal
                loading={false}
                onClose={() => {
                  setModalState(INITIAL_MODAL_STATE);
                }}
                itemName="Category"
                title="Are you sure you want to delete this trade registration"
                isOpen={modalState?.state === "delete"}
                onDelete={async () => {
                  await handleDeleteTradeRegistration(tReg._id || "");
                }}
              />
            </ButtonsSection>
          );
        })}

        <Modal
          halfWidth
          isOpen={["add", "edit"].includes(modalState?.state || "")}
          title={`${
            modalState?.state === "add" ? "Add" : "Edit"
          } Trade Registration`}
          onClose={() => {
            setModalState(INITIAL_MODAL_STATE);
            setInitialValues(INITIAL_VALUES);
          }}
        >
          <Dropdown
            options={getRegTypeOptions()}
            label="Select admin Role"
            style={{
              container: { paddingTop: 10 },
              label: { paddingBottom: 6, color: "#555A77" },
              select: {
                backgroundColor: "#F1EFFB",
                fontSize: 14,
                fontFamily: "urbanist",
                fontWeight: 500,
                padding: "8px",
              },
            }}
            onChange={(e) => {
              console.log(e.target.value);
            }}
          />{" "}
          <LabelInputField
            label="Company"
            value={initialValues.company}
            onChange={(e) => {
              setInitialValues((prev) => ({
                ...prev,
                company: e.target.value,
              }));
            }}
            name="Name"
            style={{
              label: {
                fontSize: "14px",
                fontWeight: "500px",
                color: "#555A77",
              },
              input: {
                height: 50,
                border: "1px solid #F1EFFB",
                padding: 8,
                background: "#F1EFFB",
                borderRadius: 6,
                width: "100%",
              },
              container: {
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingTop: 14,
              },
            }}
          />
          <MediaUploader
            label="Logo"
            image={
              initialValues.logo && typeof initialValues.logo === "string"
                ? [initialValues.logo]
                : []
            }
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setInitialValues((prev) => ({ ...prev, logo: file ?? "" }));
            }}
            style={{
              container: { paddingTop: 10, gap: 6, marginBottom: 10 },
              label: { paddingBottom: 10, color: "#555A77" },
              dropzone: { background: "#F1EFFB" },
            }}
          />
          <AcceptButton
            onClick={() => {
              if (modalState?.state === "add") {
                handleAddTradeRegistration();
              } else {
                handleEditTradeRegistration(initialValues?._id ?? "");
              }
            }}
            children={modalState?.state === "add" ? "Add" : "Edit"}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default TradeRegistrationTypes;
